// src/components/ui/CargaModulos.js
// Infraestructura para la CARGA DIFERIDA de módulos (code splitting del Paso 1).
// Tres piezas, todas de "cómo/cuándo se carga el código" — NO tocan lógica de negocio:
//   1) lazyConReintento — envuelve React.lazy con un reintento suave ante un
//      parpadeo de red. Si el pedazo igual no baja (típico: se desplegó una
//      versión nueva y el archivo viejo ya no existe), el error sube al guardián.
//   2) CargandoModulo — el spinner que se ve mientras baja un módulo por 1ª vez
//      (fallback de <Suspense>). Tokenizado: se ve bien en claro y oscuro.
//   3) LimiteErrorChunk — el "guardián" (ErrorBoundary): si un pedazo no carga,
//      muestra "actualizá la página" en vez de una pantalla rota. NUNCA recarga
//      solo (regla: no perder un pedido a medias); el colaborador decide con un botón.
import React from 'react';
import { ArrowPathIcon } from './Icons';

// 1) Carga diferida con reintento suave --------------------------------------
// Reintenta UNA vez tras un instante si la 1ª descarga falla. Si el 2º intento
// también falla, propaga el error original para que lo capture LimiteErrorChunk.
export function lazyConReintento(importar) {
  return React.lazy(() =>
    importar().catch((error) =>
      new Promise((resolve, reject) => {
        setTimeout(() => {
          importar().then(resolve).catch(() => reject(error));
        }, 800);
      })
    )
  );
}

// 2) Spinner de carga (fallback de Suspense) ---------------------------------
export function CargandoModulo() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '12px',
        minHeight: '60vh',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          color: 'var(--color-primario)',
          animation: 'spin 1s linear infinite',
        }}
      >
        <ArrowPathIcon className="w-8 h-8" />
      </span>
      <span style={{ color: 'var(--color-texto-secundario)', fontSize: '14px' }}>
        Cargando módulo...
      </span>
    </div>
  );
}

// 3) Guardián de errores de carga (ErrorBoundary) ----------------------------
// Distingue el caso "versión nueva desplegada" (chunk viejo que ya no existe)
// de un error genérico, y muestra el mensaje adecuado. En ambos casos ofrece
// recargar con un botón — jamás recarga por su cuenta.
function esErrorDeChunk(error) {
  if (!error) return false;
  const texto = `${error.name || ''} ${error.message || ''}`;
  return /Loading chunk|ChunkLoadError|Loading CSS chunk|dynamically imported|Failed to fetch dynamically|importing a module script failed/i.test(
    texto
  );
}

export class LimiteErrorChunk extends React.Component {
  constructor(props) {
    super(props);
    this.state = { huboError: false, esChunk: false };
  }

  static getDerivedStateFromError(error) {
    return { huboError: true, esChunk: esErrorDeChunk(error) };
  }

  componentDidCatch(error, info) {
    // Registramos para diagnóstico. A propósito NO recargamos solos.
    // eslint-disable-next-line no-console
    console.error('Error al cargar un módulo:', error, info);
  }

  recargar = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.huboError) return this.props.children;

    const esVersionNueva = this.state.esChunk;
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          minHeight: '60vh',
          padding: '24px',
          textAlign: 'center',
        }}
      >
        <span style={{ display: 'inline-flex', color: 'var(--color-primario)' }}>
          <ArrowPathIcon className="w-10 h-10" />
        </span>
        <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--color-texto)' }}>
          {esVersionNueva ? 'Salió una versión nueva del sistema' : 'No se pudo abrir esta sección'}
        </div>
        <div
          style={{
            fontSize: '14px',
            color: 'var(--color-texto-secundario)',
            maxWidth: '420px',
            lineHeight: 1.5,
          }}
        >
          {esVersionNueva
            ? 'Para seguir trabajando con la última versión, actualizá la página. No se pierde nada de lo que tengas abierto.'
            : 'Ocurrió un problema al cargar esta sección. Probá actualizar la página.'}
        </div>
        <button
          onClick={this.recargar}
          style={{
            height: '38px',
            padding: '0 20px',
            boxSizing: 'border-box',
            backgroundColor: 'var(--color-primario)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radio-sm)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          Actualizar la página
        </button>
      </div>
    );
  }
}
